import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {Collapse, DatePicker, Flex, message, Radio, theme} from "antd";
import Iconlink from "../../ui/icons/IconLink";
import {NavLink} from "react-router-dom";
import useSWRMutation from "swr/mutation";
import urls from "../../../api/urls";
import {api, HydraData} from "../../../api/apiProvider";
import dayjs from "dayjs";
import type {CSSProperties} from 'react';
import type {CollapseProps} from 'antd';
import {CaretRightOutlined} from "@ant-design/icons";
import {Criterion, Kpi, ModelType, ModelValidationResult, Product} from "../../../api/types";
import BriefData from "./briefData";
import SwitchGreen from "../../ui/inputs/SwitchGreen";
import Roles from "../../../roles/Roles";
import useSWR, {useSWRConfig} from "swr";
import getBriefParamsString from "../../../api/params";

interface Callable {
  (): void,
}

interface DescriptionProps {
  brief: BriefType,
  getBrief: Callable,
}

interface Result {
  name: string,
  code: string,
  '@id': string,
}

export interface BriefType {
  audienceText: string,
  bht: string,
  commentary: string,
  criterions: Criterion[],
  dataDateEnd: string,
  dataDateStart: string,
  dateEnd?: string,
  validationDeadline?: string,
  dateAllowed?: string,
  id: number,
  isAllowed?: boolean,
  setupValidation?: boolean,
  requiresValidation?: boolean,
  kpi: Kpi,
  modelType: ModelType,
  products: Product[],
  seasonalityText: string,
  result: ModelValidationResult,
  specificText: string,
}

const dateFormat = 'YYYY-MM-DD'

export default function Description({brief, getBrief}: DescriptionProps) {
  const {t} = useTranslation()
  const {token} = theme.useToken()
  const [results, setResults] = useState<Result[]>([])
  const { mutate } = useSWRConfig();

  const {trigger: updateBrief, isMutating: isBriefUpdating}
    = useSWRMutation(`${urls.briefs}/${brief.id}`, api.patch, /* options */)

  const panelStyle: React.CSSProperties = {
    marginBottom: 16,
    borderRadius: token.borderRadiusLG,
    border: 'none',
  }

  const {
    data: validationResults,
  } = useSWR<HydraData>(urls.validationResults, api.get, {
    revalidateOnFocus: false
  })

  useEffect(() => {
    if (validationResults && validationResults.data) {
      setResults(validationResults.data.map((validationResult: ModelValidationResult) => {
        return {
          '@id': validationResult['@id'],
          name: validationResult.name,
          code: validationResult.code,
        }
      }))
    }
  }, [validationResults])

  const onChange = (value: any, key: string) => {
    if (!Roles.hasAccess('canValidateAndAllowInSidebar')) {
      return
    }

    updateBrief({
      [key]: value
    }).then((response) => {
      if (response && response.id) {
        message.success(t('common.saved'))
        getBrief()
        mutate(`${urls.briefs}/${brief.id}?${getBriefParamsString()}`)
      }
    }).catch(errors => {
      if (errors['hydra:description']) {
        message.error(errors['hydra:description'])
      }
    })
  }

  const getItems: (panelStyle: CSSProperties) => CollapseProps['items'] = (panelStyle) => [
    {
      key: 'description',
      label: t('info.description'),
      children: <BriefData brief={brief}/>,
      style: panelStyle,
    },
  ]

  return (
    <div className={'description__wrapper'}>
      <div className={'description__item'}>
        <span className={'description__item-title'}>
          {t('info.brief')}
        </span>
        <NavLink to={`/briefs/${brief.id}/description`} className={'description__item-link'}>
          <Flex gap={6} align={'center'}>
            <Iconlink/>{t('info.goToBrief')}
          </Flex>
        </NavLink>
      </div>

      {Roles.hasAccess('canValidateAndAllowInSidebar') &&
        <>
          {brief.requiresValidation &&
            <>
              <div className={'description__item'}>
            <span className={'description__item-title'}>
              {t('brief.setupValidation')}
            </span>
                <span>
              <SwitchGreen
                value={brief.setupValidation} onChange={(value: boolean) => onChange(value, 'setupValidation')}
                size={'small'} disabled={isBriefUpdating}/>
            </span>
              </div>

              {brief.setupValidation &&
                <>
                  <div className={'description__item'}>
                <span className={'description__item-title'}>
                  {t('brief.validationDeadline')}
                </span>
                    <span>
                <DatePicker onChange={(value) => onChange(value ? value.format(dateFormat) : null, 'validationDeadline')}
                            style={{width: '150px'}}
                            value={brief.validationDeadline ? dayjs(brief.validationDeadline) : null}
                            disabled={isBriefUpdating}
                            format={'DD.MM.YYYY'}
                />
                </span>
                  </div>
                  <div className={'description__item'}>
                <span className={'description__item-title'}>
                      {t('brief.result')}
                </span>
                    <span>
                  <Radio.Group
                    name="radiogroup"
                    value={brief.result ? brief.result['@id'] : null}
                    onChange={(event) => onChange(event.target.value, 'result')}
                  >
                    {results.map((result: Result) => (
                      <Radio value={result["@id"]}>{result.name}</Radio>
                    ))}
                  </Radio.Group>
                </span>
                  </div>
                </>
              }
            </>
          }

          <div className={'description__item'}>
            <span className={'description__item-title'}>
              {t('brief.isAllowed')}
            </span>
            <span>
              <SwitchGreen value={brief.isAllowed} onChange={(value: boolean) => onChange(value, 'isAllowed')}
                           size={'small'}
                           disabled={isBriefUpdating}/>
            </span>
          </div>
          {brief.isAllowed &&
            <div className={'description__item'}>
              <span className={'description__item-title'}>
                {t('brief.dateAllowed')}
              </span>
              <span>
                <DatePicker
                  onChange={(value) => onChange(value ? value.format(dateFormat) : null, 'dateAllowed')}
                  style={{width: '150px'}}
                  value={brief.dateAllowed ? dayjs(brief.dateAllowed) : null}
                  disabled={isBriefUpdating}
                  format={'DD.MM.YYYY'}
                />
              </span>
            </div>
          }
        </>
      }

      <Collapse
        bordered={false}
        defaultActiveKey={['1']}
        expandIcon={({isActive}) => <CaretRightOutlined rotate={isActive ? 90 : 0}/>}
        style={{background: token.colorBgContainer}}
        items={getItems(panelStyle)}
        expandIconPosition={'end'}
      />
    </div>
  )
}
